import { ReactNode, useMemo, useState } from "react"
import SearchSelect from "Components/molecule/inputs/search-select"
import KpiDetailModal, { KpiDetailClick } from "Components/organisms/financial-kpi/kpi-detail-modal"
import { Container } from "reactstrap"
import Breadcrumb from "Components/organisms/breadcrumb"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import KpiMasterTable, { KPI_COLUMNS, KpiTableSection } from "Components/organisms/financial-kpi/kpi-master-table"
import ColumnVisibilityDropdown from "Components/molecule/column-visibility-dropdown"
import ReportButtons from "Components/molecule/report-buttons"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import { FinancialKpiData, KpiRow } from "models/financial-kpi"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import { formatDateTimeAmPm } from "utils/tools"

interface FinancialKpiTemplateProps {
    fiscalYear: number
    filters: ReactNode
    actions?: ReactNode
    data: FinancialKpiData
    canSaveSnapshot: boolean
    isSavingSnapshot: boolean
    onSaveSnapshot: (fiscalPeriod: number) => Promise<void>
    // The Excel workbook, offered on the Export page next to the table CSV
    fullExport?: () => void | Promise<void>
}

const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

const periodLabel = (period: number) => calendar.fiscalToLabel(Math.floor(period / 100), period % 100)

// Only drives the shared "Columns" picker (saved in this browser like every other table)
const COLUMN_DEFS: ColumnDef<KpiRow>[] = KPI_COLUMNS.map(c => ({ id: c.id, header: c.label, enableHiding: c.canHide !== false }))

// Finance > Financial KPI Metrics: fiscal year filter, then every KPI container in one master table (Excel order).
const FinancialKpiTemplate = ({ fiscalYear, filters, actions, data, canSaveSnapshot, isSavingSnapshot, onSaveSnapshot, fullExport }: FinancialKpiTemplateProps) => {
    const [confirming, setConfirming] = useState(false)
    const columnTable = useDataTable<KpiRow>({ data: [], columns: COLUMN_DEFS })
    const hiddenColumns = new Set(KPI_COLUMNS.filter(c => columnTable.getColumn(c.id)?.getIsVisible() === false).map(c => c.id))
    const [detailClick, setDetailClick] = useState<KpiDetailClick | null>(null)

    // Snapshot (funnel + WIH): saved months, the month to save (September up to the current month of the
    // current fiscal year) and the Save / Replace button.
    const status = data.snapshots
    const current = status?.currentPeriod ?? 0
    const inThisYear = Math.floor(current / 100) === fiscalYear
    const [snapshotPeriod, setSnapshotPeriod] = useState<number>(0)
    const targetPeriod = snapshotPeriod || current
    const monthOptions = useMemo(() => inThisYear
        ? Array.from({ length: current % 100 }, (_, i) => fiscalYear * 100 + i + 1)
            .map(p => ({ value: String(p), label: periodLabel(p) }))
        : [], [current, fiscalYear, inThisYear])
    const existing = status?.snapshots.find(s => s.fiscalPeriod === targetPeriod) ?? null
    const monthLabel = targetPeriod ? periodLabel(targetPeriod) : ""

    const savedMonths = (status?.snapshots ?? []).map(s => periodLabel(s.fiscalPeriod).split(" ")[0]).join(", ")
    const snapshotStatus = !status ? undefined
        : existing
            ? `${monthLabel} saved by ${existing.savedBy ?? "unknown"} on ${formatDateTimeAmPm(existing.savedAt)}`
            : savedMonths ? `Saved: ${savedMonths}` : "No snapshots saved yet; months show live values (L)"

    const save = async () => {
        setConfirming(false)
        await onSaveSnapshot(targetPeriod)
    }

    const snapshotButton = canSaveSnapshot && inThisYear && <>
        <SearchSelect
            containerStyle={{ width: 170, marginBottom: 0 }}
            options={monthOptions}
            value={String(targetPeriod)}
            onChange={v => v && setSnapshotPeriod(Number(v.value))}
            menuPortal
        />
        <PrimaryButton className="py-1" onClick={() => existing ? setConfirming(true) : save()} disabled={isSavingSnapshot}>
            {isSavingSnapshot ? "Saving..." : existing ? `Replace ${monthLabel} snapshot` : `Save ${monthLabel} snapshot`}
        </PrimaryButton>
    </>

    const sections: KpiTableSection[] = [
        {
            key: "revenue", title: "Total Annual Revenue", subtitle: "Consulting income + accrued, closed months",
            isLoading: !data.revenue, groups: [{ key: "revenue", rows: data.revenue ?? [] }],
        },
        {
            key: "client", title: "Client Revenue", subtitle: "Clients from Admin > Client Mapping",
            isLoading: !data.clientRevenue, groups: [{ key: "client", rows: data.clientRevenue ?? [] }],
            emptyMessage: "No clients yet. Add them in Admin > Client Mapping.",
        },
        {
            key: "gm", title: "Gross Margin", subtitle: "Target from Department KPIs · actuals from the Gross Margin Report",
            isLoading: !data.grossMargin,
            groups: data.grossMargin
                ? [
                    ...data.grossMargin.tabs.map(t => ({ key: t.key, title: t.label, rows: t.rows })),
                    { key: "gm-total", title: "Gross Margin Total", rows: data.grossMargin.totalRows ?? [data.grossMargin.total] },
                ]
                : [],
        },
        {
            key: "wih", title: `WIH Backlog ${fiscalYear + 1}`, subtitle: "Project forecast of the next fiscal year, frozen by the monthly snapshot",
            isLoading: !data.wih, groups: [{ key: "wih", rows: data.wih ?? [] }],
        },
        {
            key: "funnel", title: "Department Funnel", subtitle: snapshotStatus, actions: snapshotButton,
            isLoading: !data.funnel || !data.snapshots,
            groups: (data.funnel ?? []).map(g => ({ key: g.key, title: g.label, rows: g.rows })),
        },
        {
            key: "other", title: "Company KPIs", subtitle: "AR overdue, turnover, proposal win rate and billability",
            isLoading: !data.other, groups: [{ key: "other", rows: data.other ?? [] }],
        },
    ]

    const kpiCount = sections.reduce((n, s) => n + s.groups.reduce((m, g) => m + g.rows.length, 0), 0)

    return (
        <div className="page-content">
            <Container fluid style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                <Breadcrumb title="Finance" breadcrumbItem="Financial KPI Metrics" actions={actions} />
                <div className="section-container">{filters}</div>
                <div className="section-container">
                    <div className="d-print-none d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                        <span className="text-muted">{kpiCount.toLocaleString("en-CA")} KPI{kpiCount === 1 ? "" : "s"}</span>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <ColumnVisibilityDropdown table={columnTable} />
                            <ReportButtons title="Financial KPI Metrics" module="Finance" fullExport={fullExport} />
                        </div>
                    </div>
                    <KpiMasterTable
                        sections={sections}
                        hiddenColumns={hiddenColumns}
                        onCellClick={(row, period) => setDetailClick({
                            row, period, value: period === "ytd" ? row.ytd : row.months[period - 1],
                        })}
                    />
                    <small className="text-muted d-block mt-2">
                        Click a month or YTD value to see what makes it up. Funnel / WIH: <sup>S</sup> saved snapshot · <sup>L</sup> today's
                        live value (months without a snapshot; open opportunities and forecasts have no history).
                    </small>
                </div>
                <KpiDetailModal fiscalYear={fiscalYear} click={detailClick} onClose={() => setDetailClick(null)} />
                <AreYouSureModal
                    isOpen={confirming}
                    onModalClose={() => setConfirming(false)}
                    modalTitle="Replace snapshot"
                    subTitle={`Replace the ${monthLabel} snapshot with today's funnel and work in hand?`}
                    handleAreYouSureAccept={save}
                    handleAreYouSureDecline={async () => setConfirming(false)}
                    areYouSureacceptText="Replace"
                    areYouSuredeclineText="Cancel"
                />
            </Container>
        </div>
    )
}

export default FinancialKpiTemplate
